import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormNumber,
  FormRoot,
  zodResolver,
} from "@devhop/ui";
import * as RHF from "react-hook-form";
import { z } from "zod";
import type {
  LeaveEntitlementListItem,
  UpdateLeaveEntitlementDTO,
} from "@/modules/hr/leave/domain/types";

const LeaveEntitlementEditFormSchema = z.object({
  totalDays: z.number().min(0, "ວັນທີ່ມີສິດຕ້ອງບໍ່ຕໍ່າກວ່າ 0"),
  usedDays: z.number().min(0, "ວັນທີ່ໃຊ້ແລ້ວຕ້ອງບໍ່ຕໍ່າກວ່າ 0"),
  carriedOverDays: z.number().min(0, "ວັນເກັບເຂົ້າມາຕ້ອງບໍ່ຕໍ່າກວ່າ 0"),
});

export type LeaveEntitlementEditFormValues = {
  totalDays: number;
  usedDays: number;
  carriedOverDays: number;
};

type LeaveEntitlementEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entitlement: LeaveEntitlementListItem | null;
  onSubmit: (dto: UpdateLeaveEntitlementDTO) => void;
  isSubmitting?: boolean;
};

export function LeaveEntitlementEditDialog({
  open,
  onOpenChange,
  entitlement,
  onSubmit,
  isSubmitting,
}: LeaveEntitlementEditDialogProps) {
  const defaultValues: LeaveEntitlementEditFormValues = entitlement
    ? {
      totalDays: entitlement.totalDays,
      usedDays: entitlement.usedDays,
      carriedOverDays: entitlement.carriedOverDays,
    }
    : { totalDays: 0, usedDays: 0, carriedOverDays: 0 };

  const methods = RHF.useForm<LeaveEntitlementEditFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(LeaveEntitlementEditFormSchema) as any,
    defaultValues,
    values: entitlement
      ? {
        totalDays: entitlement.totalDays,
        usedDays: entitlement.usedDays,
        carriedOverDays: entitlement.carriedOverDays,
      }
      : undefined,
  });

  const handleValid = (vals: LeaveEntitlementEditFormValues) => {
    onSubmit({
      totalDays: vals.totalDays,
      usedDays: vals.usedDays,
      carriedOverDays: vals.carriedOverDays,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>ແກ້ໄຂສິດລາຕໍ່ປີ</DialogTitle>
        </DialogHeader>
        {entitlement && (
          <div className="mb-4 text-muted-foreground text-sm">
            {entitlement.employeeName} — {entitlement.leaveTypeName} (
            {entitlement.year})
          </div>
        )}
        <FormRoot<LeaveEntitlementEditFormValues>
          methods={methods}
          onSubmit={handleValid}
          className="space-y-4"
        >
          <FormNumber name="totalDays" label="ວັນທີ່ມີສິດ (totalDays)" min={0} />
          <FormNumber name="usedDays" label="ໃຊ້ແລ້ວ (usedDays)" min={0} />
          <FormNumber
            name="carriedOverDays"
            label="ເກັບເຂົ້າມາ (carriedOverDays)"
            min={0}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              ບັນທຶກ
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
